import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EAttachmentType,
  toast,
  type IChatAttachment,
  type TAttachmentType,
} from '@kognitos/lattice';

import { captureException } from '@/modules/sentry';
import { getAttachmentTypeFromFile } from '@/modules/file-renderer/utils/file-type-detection';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import type { IRainMessageDraft, IRainSendAttachment } from './types';

import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from '../constants';
import { readFileAsDataUri, RainUploadError } from '../utils/upload';
import { useUploadRainFile } from './useUploadRainFile';

/** `staged`: a document held locally until a thread exists to upload it to. */
export type TRainAttachmentStatus = 'staged' | 'uploading' | 'ready' | 'failed';

export interface IRainAttachment {
  id: string;
  file: File;
  kind: 'image' | 'document';
  name: string;
  size: number;
  mimeType: string;
  status: TRainAttachmentStatus;
  progress: number;
  dataUri?: string;
  serverUri?: string;
  previewUrl?: string;
  error?: string;
}

export interface IUseRainChatAttachmentsParams {
  agentId: string;
  /**
   * Undefined while the thread is being created. Uploads are thread-scoped, so
   * there is nothing to attach to until it exists — the composer is disabled
   * for that window, unless `deferUploadsWithoutThread` is set.
   */
  threadId: string | undefined;
  /**
   * For surfaces that create the thread on send: with no thread, documents are
   * held as `staged` instead of uploaded, and `uploadStagedDocuments` uploads
   * them once the send has created one.
   */
  deferUploadsWithoutThread?: boolean;
  /**
   * Optional path segment scoping the upload to an entity the thread belongs to
   * (e.g. `automations/{id}` or `automations/{id}/runs/{runId}`). Omit to scope
   * uploads directly to the workspace agent.
   */
  scope?: string;
}

export interface IUseRainChatAttachmentsReturn {
  attachments: IRainAttachment[];
  latticeAttachments: IChatAttachment[];
  handleFilesAdd: (files: File[]) => void;
  handleAttachmentRemove: (id: string) => void;
  handleAttachmentRetry: (id: string) => void;
  clearAttachments: () => void;
  /** Re-injects a previously cleared set of attachments (e.g. after a failed
   * send) without re-uploading them. */
  restoreAttachments: (attachments: IRainAttachment[]) => void;
  buildMessagePayload: (text: string) => IRainMessageDraft;
  /**
   * Uploads every `staged` document to `threadId` and returns them as send
   * attachments, or null if any upload failed (those are marked `failed`) or
   * was cut short by unmounting. Documents removed before or during their
   * upload are left out.
   */
  uploadStagedDocuments: (
    threadId: string,
  ) => Promise<IRainSendAttachment[] | null>;
  isUploading: boolean;
  hasFailedUploads: boolean;
}

type TDocumentUploadOutcome =
  | { status: 'ready'; uri: string }
  | { status: 'aborted' | 'failed' };

const generateAttachmentId = (): string => `rain-att-${crypto.randomUUID()}`;

const isImageFile = (file: File): boolean => file.type.startsWith('image/');

export function useRainChatAttachments({
  agentId,
  threadId,
  scope,
  deferUploadsWithoutThread = false,
}: IUseRainChatAttachmentsParams): IUseRainChatAttachmentsReturn {
  const { t } = useTranslation('rain');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { mutateAsync: uploadAsync } = useUploadRainFile();

  const [attachments, setAttachments] = useState<IRainAttachment[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const mountedRef = useRef(true);
  const attachmentsRef = useRef<IRainAttachment[]>([]);
  attachmentsRef.current = attachments;
  const stagesDocuments = !threadId && deferUploadsWithoutThread;

  const updateAttachment = useCallback(
    (id: string, patch: Partial<IRainAttachment>) => {
      if (!mountedRef.current) return;
      setAttachments(prev =>
        prev.map(att => (att.id === id ? { ...att, ...patch } : att)),
      );
    },
    [],
  );

  const handleAttachmentRemove = useCallback((id: string) => {
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }
    setAttachments(prev => {
      const target = prev.find(a => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  }, []);

  const uploadDocument = useCallback(
    async (
      attachment: IRainAttachment,
      targetThreadId: string | undefined,
    ): Promise<TDocumentUploadOutcome> => {
      const controller = new AbortController();
      abortControllersRef.current.set(attachment.id, controller);

      // Guarded rather than coerced: an upload keyed to a blank thread would
      // 404 in a way that looks like a server fault. Without a thread the
      // composer is disabled or stages documents, so reaching here is a bug.
      if (!targetThreadId) {
        throw new Error(
          'Rain attachments: upload attempted before the thread existed',
        );
      }

      try {
        const result = await uploadAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          agentId,
          threadId: targetThreadId,
          scope,
          file: attachment.file,
          signal: controller.signal,
          onProgress: (loaded, total) => {
            const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
            updateAttachment(attachment.id, { progress });
          },
        });
        abortControllersRef.current.delete(attachment.id);
        updateAttachment(attachment.id, {
          status: 'ready',
          progress: 100,
          serverUri: result.uri,
        });
        return { status: 'ready', uri: result.uri };
      } catch (err) {
        abortControllersRef.current.delete(attachment.id);
        if (err instanceof RainUploadError && err.kind === 'aborted') {
          return { status: 'aborted' };
        }
        const httpStatus =
          err instanceof RainUploadError ? err.httpStatus : undefined;
        const message =
          httpStatus === 413
            ? t('rain.error.fileTooLarge', {
                filename: attachment.name,
                maxMb: MAX_FILE_SIZE_MB,
              })
            : httpStatus === 404
              ? t('rain.error.threadMissing')
              : t('rain.error.uploadFile', {
                  filename: attachment.name,
                });
        updateAttachment(attachment.id, {
          status: 'failed',
          error: message,
        });
        toast.error(message);
        captureException(err as Error, {
          tags: { module: 'rain-chat', action: 'upload_file' },
          extra: { httpStatus, filename: attachment.name },
        });
        return { status: 'failed' };
      }
    },
    [
      agentId,
      currentOrganizationId,
      currentWorkspaceId,
      scope,
      t,
      updateAttachment,
      uploadAsync,
    ],
  );

  const readImage = useCallback(
    async (attachment: IRainAttachment) => {
      try {
        const dataUri = await readFileAsDataUri(attachment.file);
        updateAttachment(attachment.id, {
          status: 'ready',
          progress: 100,
          dataUri,
        });
      } catch (err) {
        const message = t('rain.error.readImage', {
          filename: attachment.name,
        });
        updateAttachment(attachment.id, {
          status: 'failed',
          error: message,
        });
        toast.error(message);
        captureException(err as Error, {
          tags: { module: 'rain-chat', action: 'read_image' },
          extra: { filename: attachment.name },
        });
      }
    },
    [t, updateAttachment],
  );

  const handleFilesAdd = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const accepted: IRainAttachment[] = [];

      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(
            t('rain.error.fileTooLarge', {
              filename: file.name,
              maxMb: Math.round(MAX_FILE_SIZE_BYTES / 1024 / 1024),
            }),
          );
          continue;
        }

        const image = isImageFile(file);
        const previewUrl = image ? URL.createObjectURL(file) : undefined;
        accepted.push({
          id: generateAttachmentId(),
          file,
          kind: image ? 'image' : 'document',
          name: file.name,
          size: file.size,
          mimeType: file.type,
          status: !image && stagesDocuments ? 'staged' : 'uploading',
          progress: 0,
          previewUrl,
        });
      }

      if (accepted.length === 0) return;
      setAttachments(prev => [...prev, ...accepted]);

      for (const att of accepted) {
        if (att.kind === 'image') {
          void readImage(att);
        } else if (att.status === 'uploading') {
          void uploadDocument(att, threadId);
        }
      }
    },
    [readImage, stagesDocuments, t, threadId, uploadDocument],
  );

  const handleAttachmentRetry = useCallback(
    (id: string) => {
      const target = attachmentsRef.current.find(a => a.id === id);
      if (!target || target.status !== 'failed') return;
      if (abortControllersRef.current.has(id)) return;
      const status: TRainAttachmentStatus =
        target.kind === 'document' && stagesDocuments ? 'staged' : 'uploading';
      updateAttachment(id, { status, progress: 0, error: undefined });
      const next = { ...target, status, progress: 0 };
      if (target.kind === 'image') {
        void readImage(next);
      } else if (status === 'uploading') {
        void uploadDocument(next, threadId);
      }
    },
    [readImage, stagesDocuments, threadId, updateAttachment, uploadDocument],
  );

  const uploadStagedDocuments = useCallback(
    async (targetThreadId: string): Promise<IRainSendAttachment[] | null> => {
      const staged = attachmentsRef.current.filter(a => a.status === 'staged');
      for (const att of staged) {
        updateAttachment(att.id, { status: 'uploading', progress: 0 });
      }
      const outcomes = await Promise.all(
        staged.map(att => uploadDocument(att, targetThreadId)),
      );
      const uploaded: IRainSendAttachment[] = [];
      for (const [index, att] of staged.entries()) {
        const outcome = outcomes[index];
        if (outcome.status === 'failed') return null;
        // Unmounting aborts uploads too; only a mounted abort means "removed".
        if (outcome.status === 'aborted' && !mountedRef.current) return null;
        if (outcome.status === 'ready') {
          uploaded.push({
            kind: 'document',
            uri: outcome.uri,
            filename: att.name,
          });
        }
      }
      return uploaded;
    },
    [updateAttachment, uploadDocument],
  );

  const clearAttachments = useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    setAttachments(prev => {
      for (const a of prev) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      }
      return [];
    });
  }, []);

  const restoreAttachments = useCallback((restored: IRainAttachment[]) => {
    if (restored.length === 0) return;
    // clearAttachments revoked the original image preview blob URLs, so mint
    // fresh ones from the retained File. Documents reference serverUri/dataUri
    // and need no blob URL. No upload is re-issued — the attachments come back
    // exactly as they were sent.
    setAttachments(
      restored.map(att =>
        att.kind === 'image'
          ? { ...att, previewUrl: URL.createObjectURL(att.file) }
          : att,
      ),
    );
  }, []);

  // eslint-disable-next-line no-restricted-syntax -- reason: Lifecycle cleanup must abort all in-flight uploads and revoke blob URLs on unmount
  useEffect(() => {
    // Reset on (re)mount so a prior unmount — e.g. StrictMode's dev
    // mount→unmount→remount cycle — doesn't leave this stuck false, which would
    // make updateAttachment a no-op and freeze uploads at "uploading" forever.
    mountedRef.current = true;
    const controllers = abortControllersRef.current;
    return () => {
      mountedRef.current = false;
      for (const controller of controllers.values()) {
        controller.abort();
      }
      controllers.clear();
      for (const a of attachmentsRef.current) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      }
    };
  }, []);

  const isUploading = useMemo(
    () => attachments.some(a => a.status === 'uploading'),
    [attachments],
  );
  const hasFailedUploads = useMemo(
    () => attachments.some(a => a.status === 'failed'),
    [attachments],
  );

  const latticeAttachments = useMemo<IChatAttachment[]>(
    () =>
      attachments.map(att => {
        const type: TAttachmentType =
          att.kind === 'image'
            ? EAttachmentType.IMAGE
            : getAttachmentTypeFromFile(att.file);
        return {
          id: att.id,
          name: att.name,
          type,
          url: att.previewUrl ?? att.dataUri,
          size: att.size,
        };
      }),
    [attachments],
  );

  const buildMessagePayload = useCallback(
    (text: string): IRainMessageDraft => {
      const ready: IRainSendAttachment[] = attachments
        .filter(a => a.status === 'ready')
        .map(a => {
          if (a.kind === 'image' && a.dataUri) {
            return { kind: 'image', uri: a.dataUri };
          }
          const uri = a.serverUri ?? a.dataUri;
          return uri ? { kind: 'document', uri, filename: a.name } : null;
        })
        .filter((x): x is IRainSendAttachment => x !== null);

      const draft: IRainMessageDraft = { content: text.trim() };
      if (ready.length > 0) draft.attachments = ready;
      return draft;
    },
    [attachments],
  );

  return {
    attachments,
    latticeAttachments,
    handleFilesAdd,
    handleAttachmentRemove,
    handleAttachmentRetry,
    clearAttachments,
    restoreAttachments,
    buildMessagePayload,
    uploadStagedDocuments,
    isUploading,
    hasFailedUploads,
  };
}
